// Library Methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DryEyeDescription from "./DryEyeDescription/DryEyeDescription";
import AddEditDryEyeModal from "./AddEditDryEyeModal/AddEditDryEyeModal";
import DeleteDryEyeModal from "./DeleteDryEyeModal/DeleteDryEyeModal";
import { PlusIcon } from "src/assets/icons";

// Types
import {
  AddEditDryEyeType,
  DryEyeProfileType,
} from "src/services/clinics/types";

// Styles
import {
  ModalCustomConfirmDarkCta,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";
import { useTheme } from "@mui/material/styles";

// Hooks
import { useDryEyeProfiles } from "src/services/clinics/hooks/clinic-lenses/dry-eyes/useDryEyeProfiles";
import { useAddDryEyeProfile } from "src/services/clinics/hooks/clinic-lenses/dry-eyes/useAddDryEyeProfile";
import { useUpdateDryEyeProfile } from "src/services/clinics/hooks/clinic-lenses/dry-eyes/useUpdateDryEyeProfile";
import { useDeleteDryEyeProfile } from "src/services/clinics/hooks/clinic-lenses/dry-eyes/useDeleteDryEyeProfile";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type DryEyeSettingsType = {
  isLoadingSettings: boolean;
  isUpdatingSettings: boolean;
};

const DryEyeSettings = ({
  isLoadingSettings,
  isUpdatingSettings,
}: DryEyeSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const [selectedProfile, setSelectedProfile] = useState<DryEyeProfileType>();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const {
    data: profileData,
    isLoading: isLoadingProfiles,
    isError: isErrorProfiles,
    isFetching: isFetchingProfiles,
    isRefetching: isRefetchingProfiles,
  } = useDryEyeProfiles(clinicInfo?.id);

  const { mutate: addProfileMutate, isPending: addProfilePending } =
    useAddDryEyeProfile(clinicInfo?.id);
  const { mutate: updateProfileMutate, isPending: updateProfilePending } =
    useUpdateDryEyeProfile(clinicInfo?.id);
  const { mutate: deleteProfileMutate, isPending: deleteProfilePending } =
    useDeleteDryEyeProfile(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingSettings || isLoadingProfiles,
    [isLoadingSettings, isLoadingProfiles]
  );

  const isUpdating = useMemo(
    () =>
      isUpdatingSettings ||
      isFetchingProfiles ||
      isRefetchingProfiles ||
      addProfilePending ||
      updateProfilePending ||
      deleteProfilePending,
    [
      isUpdatingSettings,
      isFetchingProfiles,
      isRefetchingProfiles,
      addProfilePending,
      updateProfilePending,
      deleteProfilePending,
    ]
  );

  const hasProfiles = useMemo(
    () =>
      !isLoadingProfiles &&
      !isErrorProfiles &&
      profileData &&
      profileData?.length > 0,
    [isLoadingProfiles, isErrorProfiles, profileData]
  );

  const hasNoProfiles = useMemo(
    () => !isLoadingProfiles && !isErrorProfiles && !profileData?.length,
    [isLoadingProfiles, isErrorProfiles, profileData]
  );

  const addProfile = useCallback(
    (data: AddEditDryEyeType) => {
      addProfileMutate(data, { onSuccess: () => setOpenAddModal(false) });
    },
    [addProfileMutate]
  );

  const updateProfile = useCallback(
    (data: AddEditDryEyeType) => {
      if (!selectedProfile) return;

      updateProfileMutate(
        { ...selectedProfile, ...data },
        {
          onSuccess: () => {
            setSelectedProfile(undefined);
            setOpenEditModal(false);
          },
        }
      );
    },
    [updateProfileMutate, selectedProfile]
  );

  const deleteProfile = useCallback(() => {
    if (!selectedProfile) return;

    deleteProfileMutate(selectedProfile?.id, {
      onSuccess: () => {
        setSelectedProfile(undefined);
        setOpenDeleteModal(false);
      },
    });
  }, [deleteProfileMutate, selectedProfile]);

  return (
    <>
      <DefaultCard>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="space-between" gap="16px">
            <Box display="flex" flexDirection="column" gap="6px">
              <Typography fontSize="14px" fontWeight={600}>
                {t("Dry Eye Options")}
              </Typography>

              <Typography fontSize="14px" color={mutedText}>
                {t(
                  "Manage virtual lenses that can be used in lens simulations."
                )}
              </Typography>
            </Box>
            <Button
              disabled={isLoading || isUpdating}
              sx={ModalCustomConfirmDarkCta}
              startIcon={<PlusIcon />}
              onClick={() => setOpenAddModal(true)}
            >
              {t("Add")}
            </Button>
          </Box>

          {isLoading ? (
            <Box display="flex" flexDirection="column" gap="20px">
              <Skeleton
                variant="rounded"
                width="100%"
                height={UI_SIZES.inputHeight}
                sx={{
                  borderRadius: "4px",
                }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={90}
                sx={{
                  borderRadius: "4px",
                }}
              />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap="20px">
              {hasNoProfiles && (
                <Alert
                  variant="warning"
                  alertContent={t("No lens options found.")}
                />
              )}

              {hasProfiles && (
                <Box display="flex" flexWrap="wrap" gap="12px">
                  {profileData?.map((profile, index) => (
                    <Box
                      key={`${profile.name}-${index}`}
                      sx={{
                        flexGrow: 1,
                        flexBasis: {
                          xs: "100%",
                          md: "40%",
                        },
                        minWidth: 0,
                        "& > *": { height: "100%" },
                      }}
                    >
                      <DryEyeDescription
                        isUpdating={isUpdating}
                        profileData={profile}
                        openDeleteModal={() => {
                          setSelectedProfile(profile);
                          setOpenDeleteModal(true);
                        }}
                        openEditModal={() => {
                          setSelectedProfile(profile);
                          setOpenEditModal(true);
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DefaultCard>

      <AddEditDryEyeModal
        open={openAddModal}
        title={t("Add Dry Eye Option")}
        onClose={() => setOpenAddModal(false)}
        onConfirm={addProfile}
        isUpdating={isUpdating}
      />

      <AddEditDryEyeModal
        open={openEditModal}
        title={t("Edit Dry Eye Option")}
        profileToEdit={selectedProfile}
        onClose={() => {
          setSelectedProfile(undefined);
          setOpenEditModal(false);
        }}
        onConfirm={updateProfile}
        isUpdating={isUpdating}
      />

      <DeleteDryEyeModal
        isLoading={isUpdating}
        selectedProfile={selectedProfile}
        open={openDeleteModal}
        onClose={() => {
          setSelectedProfile(undefined);
          setOpenDeleteModal(false);
        }}
        onConfirm={deleteProfile}
      />
    </>
  );
};

export default DryEyeSettings;
